import { AxiosPromise } from 'axios';
import { ResponseDefault } from '@app/typings/services/service.d';
import { PaginationParams } from '@app/typings/default';
import { EvLockerType } from './evlockers';

export enum ChargerLockerType {
  NIU = 'niu',
  UNIVERSAL = 'universal',
}

export enum NiuBatteryStatus {
  DISCHARGE = 'discharge',
  CHARGE = 'charge',
  MOS_DAMAGE = 'mos_damage',
  NO_LOAD = 'no_load',
  OVER_CHARGE = 'over_charge',
  OVER_DISCHARGE = 'over_discharge',
  OVER_CURRENT_CHARGE = 'over_current_charge',
  OVER_CURRENT_DISCHARGE = 'over_current_discharge',
  OVERHEAT = 'overheat',
  OTHER_EXCEPTION = 'other_exception',
  SHORT = 'short',
  SUPER_COOL = 'super_cool',
  UNKNOWN = 'unknown',
  WATER = 'water',
}

export enum UniversalBatteryStatus {
  OK = 'ok',
  UNDER_VOLT = 'under_volt',
  OVER_VOLT = 'over_volt',
  UNDER_CURRENT = 'under_current',
  OVER_CURRENT = 'over_current',
  UNDER_TEMP = 'under_temp',
  OVER_TEMP = 'over_temp',
  BMS_FAULT = 'bms_fault',
  UNBALANCED = 'unbalanced',
  SHORT = 'short',
}

export enum NiuChargerStatus {
  DISABLED = 'DISABLED',
  ERROR = 'ERROR',
  FAST_RECOVERY = 'FAST_RECOVERY',
  FULL = 'FULL',
  IDLE = 'IDLE',
  NO_POWER = 'NO_POWER',
  NORMAL_CHARGE = 'NORMAL_CHARGE',
  SLOW_RECOVERY = 'SLOW_RECOVERY',
  SOFT_FULL = 'SOFT_FULL',
  UNKNOWN = 'UNKNOWN',
  WARNING = 'WARNING',
  WARN_FLAG = 'WARN_FLAG',
  WARN_UNB = 'WARN_UNB',
  ERR_NORM_TO = 'ERR_NORM_TO',
  ERR_SLOW_TO = 'ERR_SLOW_TO',
  ERR_FLAG = 'ERR_FLAG',
  ERR_UNB = 'ERR_UNB',
  ERR_RS485_FAULT = 'ERR_RS485_FAULT',
}

export enum UniversalChargerStatus {
  CHARGE_1 = 'CHARGE_1',
  CHARGE_2 = 'CHARGE_2',
  DISABLE = 'DISABLE',
  DOOR_CLOSE = 'DOOR_CLOSE',
  DOOR_OPEN = 'DOOR_OPEN',
  ERR_BAT_FAULT = 'ERR_BAT_FAULT',
  ERR_CHG_FAULT = 'ERR_CHG_FAULT',
  ERR_CHG_TIMEOUT = 'ERR_CHG_TIMEOUT',
  ERR_PCHG_TIMEOUT = 'ERR_PCHG_TIMEOUT',
  ERR_RCHG_TIMEOUT = 'ERR_RCHG_TIMEOUT',
  ERR_TEMP_TIMEOUT = 'ERR_TEMP_TIMEOUT',
  FULL = 'FULL',
  NO_CHARGER = 'NO_CHARGER',
  PRE_CHARGE = 'PRE_CHARGE',
  RECHARGE = 'RECHARGE',
  R_MEAS = 'R_MEAS',
  SOFT_FULL = 'SOFT_FULL',
  UNKNOWN = 'UNKNOWN',
  WAIT_TEMP_BAT = 'WAIT_TEMP_BAT',
  WAIT_TEMP_CHG = 'WAIT_TEMP_CHG',
  ERR_BAT_RS485_FAULT = 'ERR_BAT_RS485_FAULT',
}

export type BatteryStatus = NiuBatteryStatus | UniversalBatteryStatus;
export type ChargerStatus = NiuChargerStatus | UniversalChargerStatus;

export interface BatterySearchParams {
  from?: string;
  to?: string;
  statuses?: string[];
  serialNumber?: string;
  station?: string;
}

export interface FilterBatteries {
  filters?: BatterySearchParams;
  pages: PaginationParams;
}

export interface CreateBatteryParams {
  serial: string;
  type: EvLockerType;
}

export interface BatteryData {
  temp5: number;
  _id: string;
  temp4: number;
  temp3: number;
  temp2: number;
  temp1: number;
  type: EvLockerType;
  timeToFullCharge: number;
  timeToFullChargeString: string;
  status: NiuBatteryStatus[] | UniversalBatteryStatus[];
  state: string;
  stateOfCharge: number;
  dischargingCurrent: number;
  chargerStatus: NiuChargerStatus | UniversalChargerStatus;
  chargingCurrent: number;
  chargingVoltage: number;
  cycleCount: number;
  capacity: number;
  serialNumber: string;
  createdAt: string;
  updatedAt: string;
  carId: string;
  currentEvLocker?: CurrentEvLockerInfo;
}

interface UserInfo {
  profilePicture: string;
  name: string;
}

interface TransactionInfo {
  action: string;
  actionAt: string;
  user?: UserInfo;
}

interface ProjectInfo {
  name?: string;
  picture?: string;
}

interface CurrentEvLockerInfo {
  lockerNumber: number;
  transactionInfo?: TransactionInfo;
  project?: ProjectInfo;
  projectId: string;
}

interface BatteryResponse extends ResponseDefault {
  data: BatteryData[];
}

export interface FetchBatteryParams {
  offset?: number;
  limit?: number;
  serialNumber?: string;
  from?: string;
  to?: string;
  projectId?: string;
  status?: string;
}

export type FetchBattery = (params: FilterBatteries) => AxiosPromise<BatteryResponse>;

export type CreateBattery = (params: CreateBatteryParams) => AxiosPromise<BatteryResponse>;

export interface BatteriesSyncData {
  status: boolean;
}

interface SyncBatteriesResponse extends ResponseDefault {
  data: BatteriesSyncData;
}

export type FetchSyncBatteries = () => AxiosPromise<SyncBatteriesResponse>;

export type SyncBatteries = (event: boolean) => AxiosPromise<SyncBatteriesResponse>;

export type SyncBatteriesNow = () => AxiosPromise<SyncBatteriesResponse>;
